import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Col, Form, Input, Pagination, Row, Select, Spin} from "antd";
import {createCompany, getCompany, getGuarDocCompanies, useCompany, useCompanyCreate} from "../hooks/company";
import {CompanyCreateEditDto, CompanyDto, CompanyListItemDto} from "../generated-api";
import {useHistory} from "react-router-dom";
import {NotDefined} from "../components/NotDefined";
import {DEFAULT_PAGE_SIZE} from "../constants";
import {Briefcase, Filter, PlusCircle} from "react-feather";
import {BackButton} from "../components/layout/BackButton";
import {getBusinessSectors, useBusinessSectors} from "../hooks/businessSectors";
import {filtersSubject, useFilters} from "../hooks";
import {COMPANY_STATUS_ACTIVE, COMPANY_STATUS_DISABLED} from "../constants/company";
const pageSize = DEFAULT_PAGE_SIZE;

const validateUnique = (companies: any[], propertyName: string, value: string, t: any): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const valid = !companies.find(item => String(item[propertyName]).toLowerCase() === String(value).toLowerCase());
        if (valid) {
            resolve(true);
        } else {
            reject(t('error.valueNotUnique'));
        }
    });
};

export function CompaniesPage() {
    const {t} = useTranslation();
    const history = useHistory();
    useEffect(() => {
        getBusinessSectors().then().catch(console.error);
        getCompany().then().catch(console.error);
    }, [true]);
    const companyResponse = useCompany();
    const companies = companyResponse.data || [];
    const companyCreateResponse = useCompanyCreate();
    const businessSectorsResponse = useBusinessSectors();
    const businessSectors = (businessSectorsResponse || {}).data || [];
    const [page, setPage] = useState(1);
    const [addFormCollapsed, setAddFormCollapsed] = useState(true);
    const [form] = Form.useForm();
    const filters: any = useFilters();
    const [filterCollapsed, setFilterCollapsed] = useState(!filters.filterBusinessSector);
    const [filterBusinessSector, setFilterBusinessSector] = useState<any>(filters.filterBusinessSector);
    const [filterStatus, setFilterStatus] = useState<any>(filters.filterStatus);

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };

    const onFinish = (values: CompanyCreateEditDto): void => {
        createCompany(values).then((company: CompanyDto) => {
            history.push('/companies/' + company.id);
        }).catch(console.error);
    };

    const filtered = getGuarDocCompanies(companies, 1).filter((item: CompanyListItemDto) => 
        (filterBusinessSector ? item.businessSectorId === filterBusinessSector : true)
        && 
        ([COMPANY_STATUS_ACTIVE, COMPANY_STATUS_DISABLED].includes(filterStatus) ?  item.status === filterStatus : true)
    );

    const processed = [];
    for (let i = (page - 1) * pageSize; i < page * pageSize; i++) {
        filtered[i] && processed.push(filtered[i]);
    }

    return (
        <Spin spinning={companyResponse.loading}>
            <PageHeader/>
            <div className={'top-block accent3-background'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton/><Briefcase/>{t('companies.title')}</h1>
                    <a href={'/companies'} className={'title-icon-button mr-1 ' + (filterCollapsed ? '' : 'toggle')}
                       onClick={event => {
                           event.preventDefault();
                           setFilterCollapsed(!filterCollapsed);
                           setAddFormCollapsed(true);
                       }}>
                        <Filter/>
                    </a>
                    <a href={'/companies/add'} className={'title-icon-button ' + (addFormCollapsed ? '' : 'toggle')}
                       onClick={event => {
                           event.preventDefault();
                           setAddFormCollapsed(!addFormCollapsed);
                           setFilterCollapsed(true);
                           form.resetFields();
                       }}>
                        <PlusCircle className={addFormCollapsed ? '' : 'rotate45'}/>
                    </a>
                </div>
                <Form className={filterCollapsed ? 'collapsed' : 'expanded'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} lg={8}>
                            <Form.Item className={'mb-0'}>
                                <Select placeholder={t('company.businessSector')} allowClear showSearch
                                        style={{width: '100%'}}
                                        value={filterBusinessSector}
                                        onChange={filterBusinessSector => {
                                            setFilterBusinessSector(filterBusinessSector);
                                            filtersSubject.next({filterBusinessSector});
                                        }}
                                        filterOption={(input, option): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                    {businessSectors.map((item: any) =>
                                        <Select.Option key={item.id} value={String(item.id)}>{item.name}</Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} lg={8}>
                            <Form.Item className={'mb-0'}>
                                <Select placeholder={ t('company.status.title')} allowClear
                                        style={{width: '100%'}}
                                        onChange={filterStatus => {
                                            setFilterStatus(filterStatus);
                                            filtersSubject.next({filterStatus});
                                        }}
                                >
                                    <Select.Option key={COMPANY_STATUS_ACTIVE} value={COMPANY_STATUS_ACTIVE}>
                                        {t('company.status.active')}
                                    </Select.Option>
                                    <Select.Option key={COMPANY_STATUS_DISABLED} value={COMPANY_STATUS_DISABLED}>
                                        {t('company.status.disabled')}
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Spin spinning={companyCreateResponse.loading}>
                    <Form form={form} name="company" onFinish={onFinish} validateMessages={validateMessages}
                          autoComplete={'off'} className={addFormCollapsed ? 'collapsed' : 'expanded'}
                          validateTrigger={'onSubmit'}>
                        <Row className={'row-offset'}>
                            <Col span={24} md={12} lg={6}>
                                <Form.Item name={'businessSectorId'}
                                           rules={[{required: true}]}>
                                    <Select allowClear showSearch style={{width: '100%'}}
                                            placeholder={t('company.businessSector')}
                                            filterOption={(input, option): any =>
                                                String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                            }>
                                        {businessSectors.map((item: any) =>
                                            <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12} lg={6}>
                                <Form.Item name={'name'}
                                           rules={[{required: true}, {
                                               validator: (rule, value) => validateUnique(companies, 'name', value, t)
                                           }, {max: 512}]}>
                                    <Input placeholder={t('company.name')}/>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12} lg={6}>
                                <Form.Item name={'registryCode'}
                                           rules={[{required: true}, {max: 50}]}>
                                    <Input placeholder={t('company.registryCode')}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item className={'no-margin'}>
                            <Button type="primary" className={'white-background text-default w-100'} htmlType="submit">
                                {t('companies.add')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Spin>
            </div>
            <div className={'subpage-content'}>
                {processed.map((company: CompanyListItemDto, index: number) =>
                    <div key={index} className={`card accent3-background ${company.status !== COMPANY_STATUS_ACTIVE ? 'card-disabled' : ''}`}>
                        <a href={'/companies/' + company.id} onClick={event => {
                            event.preventDefault();
                            history.push('/companies/' + company.id);
                        }}>
                            <div className={'card-header flex-row'}>
                                {company.name}
                            </div>
                            <div className={'card-body'}>
                                <Row className={'row-offset'}>
                                    <Col span={12} lg={5}>
                                        <label>
                                            {t('company.registryCode')}:
                                        </label>
                                        <p>
                                            {company.registryCode}
                                        </p>
                                    </Col>
                                    <Col span={12} lg={5}>
                                        <label>
                                            {t('company.vatNumber')}:
                                        </label>
                                        <p>
                                            {company.vatNumber || <NotDefined/>}
                                        </p>
                                    </Col>
                                    {!!company.generalPhone && <Col span={24} md={12} lg={6}>
                                        <label>
                                            {t('company.generalPhone')}:
                                        </label>
                                        <p>
                                            {company.generalPhone}
                                        </p>
                                    </Col>}
                                    {!!company.generalEmail && <Col span={24} md={12} lg={8}>
                                        <label>
                                            {t('company.generalEmail')}:
                                        </label>
                                        <p>
                                            {company.generalEmail}
                                        </p>
                                    </Col>}
                                </Row>
                            </div>
                        </a>
                    </div>)
                }
                {!processed.length && <div className={'no-data'}>{t('general.noData')}</div>}
                {filtered.length > pageSize &&
                <Pagination onChange={setPage} pageSize={pageSize} total={filtered.length} current={page}
                            defaultCurrent={page}/>
                }
            </div>
        </Spin>
    )
}
